import { TDnsRecord, TDnsRecordBody, TNsStatus, TRawScanRecord } from './types'

// Shown if POST /domains doesn't return its own list
export const DEFAULT_NAMESERVERS = [
 'ns1.agentroof.ca',
 'ns2.agentroof.ca',
 'ns3.agentroof.ca',
]

export const PURCHASE_DOMAIN_URL = 'https://domains.agentroof.com/'

// legacy_connected renders exactly like ns_verified
export const isConnected = (status?: TNsStatus): boolean =>
 status === 'ns_verified' || status === 'legacy_connected'

// Server message for 4xx responses (e.g. "managed by AgentRoof"), else fallback
export const getErrorMessage = (err: any, fallback: string): string =>
 err?.data?.message || err?.message || fallback

// Accepts both plain JSON and the { status, data } wrapper some domain
// endpoints now return
export const unwrapResponse = <T>(res: any): T =>
 res && typeof res === 'object' && 'status' in res && 'data' in res
  ? res.data
  : res

// "agentroof.ca" -> "@", "s1.domainkey.agentroof.ca" -> "s1.domainkey"
const toRelativeHost = (host: string, domain: string): string => {
 const name = host.toLowerCase().replace(/\.$/, '')
 if (name === domain) return '@'
 return name.endsWith(`.${domain}`) ? name.slice(0, -(domain.length + 1)) : name
}

// Maps a raw scan record to the spec shape { type, host, value, priority }.
// Records already in that shape pass through unchanged
export const toScanRecord = (
 r: TRawScanRecord,
 domain: string,
): TDnsRecordBody => {
 const value =
  r.value ?? r.ip ?? r.ipv6 ?? r.target ?? r.txt ?? r.entries?.join('') ?? ''
 const priority = r.priority ?? r.pri
 return {
  type: r.type,
  host: toRelativeHost(r.host, domain),
  value,
  ...(r.type === 'MX' ? { priority: priority ?? null } : {}),
 }
}

// Drops records with no usable value (e.g. a null MX with an empty target)
export const toScanRecords = (
 records: TRawScanRecord[] | undefined,
 domain: string,
): TDnsRecordBody[] =>
 (records ?? [])
  .map((r) => toScanRecord(r, domain))
  .filter((r) => r.value !== '')

export const formatRecordValue = (record: TDnsRecord): string =>
 record.type === 'MX' && record.priority != null
  ? `${record.value} (priority ${record.priority})`
  : record.value

// Drops UI-only fields (id, protected) before sending a record to the API
export const toRecordBody = (record: TDnsRecord): TDnsRecordBody => ({
 type: record.type,
 host: record.host,
 value: record.value,
 ...(record.type === 'MX' ? { priority: record.priority ?? null } : {}),
})

export const copyToClipboard = (value: string): void => {
 if (navigator.clipboard?.writeText) {
  navigator.clipboard.writeText(value).catch(() => undefined)
  return
 }
 // Fallback for non-secure contexts without the Clipboard API
 const temp = document.createElement('textarea')
 temp.value = value
 temp.style.position = 'fixed'
 temp.style.opacity = '0'
 document.body.appendChild(temp)
 temp.select()
 document.execCommand('copy')
 document.body.removeChild(temp)
}
